import { useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useMonthlySummary } from "../../../api/queries";
import { useInvoices } from "../../../api/lists";
import { SummaryDisplay } from "../../SummaryDisplay";
import { CenteredSpinner } from "../../CenteredSpinner";
import type { InvoiceEntity, InvoiceId } from "../../../api/types";
import { InvoiceDetails } from "./InvoiceDetails";

export type InvoiceSummaryModalProps = {
  invoiceId: InvoiceId;
  open: boolean;
  onClose: () => void;
  /** Open the invoice's edit form, with the stored invoice. */
  onEdit: (invoice: InvoiceEntity) => void;
};

/**
 * The read-only view of an invoice: its own fields and attachments, then a
 * summary of the transactions linked to it. Opened by `?invoice=<id>` on the
 * Invoices page, which is where every link to an invoice leads.
 */
export const InvoiceSummaryModal = ({
  invoiceId,
  open,
  onClose,
  onEdit,
}: InvoiceSummaryModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [viewMode, setViewMode] = useState<
    "groups" | "accounts" | "categories"
  >("groups");

  // The stored invoices, not the registry's: its projection leaves out the
  // amounts, KID and pay-to account, and the edit form saves whatever it is
  // handed — a registry copy there would wipe those four.
  const { data: invoices, isPending: isInvoicePending } = useInvoices();
  const invoice = invoices?.find((i) => i.id === invoiceId);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    error,
  } = useMonthlySummary({ type: "invoice", invoiceId });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{invoice?.name ?? "Invoice"}</DialogTitle>
      <DialogContent dividers>
        {isInvoicePending ? (
          <CenteredSpinner />
        ) : invoice ? (
          <InvoiceDetails invoice={invoice} />
        ) : (
          <Alert severity="warning">This invoice no longer exists.</Alert>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3">
            Summary
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="groups" aria-label="groups">
              Groups
            </ToggleButton>
            <ToggleButton value="accounts">Kort / Sparing</ToggleButton>
            <ToggleButton value="categories" aria-label="categories">
              Categories
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <SummaryDisplay
          summary={summary}
          isLoading={isSummaryLoading}
          error={error}
          viewMode={viewMode}
        />
      </DialogContent>
      <DialogActions>
        {invoice && (
          <Button startIcon={<EditIcon />} onClick={() => onEdit(invoice)}>
            Edit
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
